import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { Box, IconButton, TextField } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import type { FilterControlProps, FilterOperator } from '../filters/filter-registry.js';
import {
  FILTER_OPERATORS,
  getOperatorsForType,
  registerFilterControl,
} from '../filters/filter-registry.js';
import { FilterOperatorMenu } from './FilterOperatorMenu.tsx';
import {
  normalizeDateFilterInput,
  serializeDateFilterValue,
} from '../../../../runtime/dates/date-semantics';

type DateFilterProps = FilterControlProps;

export function DateColumnFilter({ column, value, onChange, disabled }: DateFilterProps) {
  const { t } = useTranslation();
  const accessibleLabel = t(resolveTKey(column.labelKey));
  const rangeEndLabel = `${accessibleLabel} ${t(resolveTKey('filters.to'))}`;
  const inputType: 'date' | 'datetime-local' = column.fieldType === 'datetime' ? 'datetime-local' : 'date';
  const fieldType = column.fieldType === 'datetime' ? 'datetime' : 'date';
  const allowedOperators = useMemo(() => {
    const candidates = getOperatorsForType(fieldType);
    const allowed = column.allowedFilterOperators;
    return allowed ? candidates.filter(operator => allowed.includes(operator.id)) : candidates;
  }, [column.allowedFilterOperators, fieldType]);
  const defaultOperator = allowedOperators.find(operator => operator.id === 'on')?.id
    ?? allowedOperators.find(operator => operator.id === 'equals')?.id
    ?? allowedOperators[0]?.id
    ?? 'equals';
  const [localValue, setLocalValue] = useState('');
  const [localEndValue, setLocalEndValue] = useState('');
  const [localOperator, setLocalOperator] = useState<FilterOperator>(value?.operator ?? defaultOperator);

  useEffect(() => {
    if (value) {
      setLocalOperator(value.operator);
      if (value.operator === 'between' && Array.isArray(value.value)) {
        setLocalValue(normalizeDateFilterInput(value.value[0], fieldType));
        setLocalEndValue(normalizeDateFilterInput(value.value[1], fieldType));
      } else {
        setLocalValue(normalizeDateFilterInput(value.value, fieldType));
        setLocalEndValue('');
      }
    } else {
      setLocalValue('');
      setLocalEndValue('');
      setLocalOperator(defaultOperator);
    }
  }, [defaultOperator, inputType, value]);

  const operatorDescriptor =
    FILTER_OPERATORS.find(operator => operator.id === localOperator) ?? allowedOperators[0];
  const isRange = localOperator === 'between';
  const requiresValue = operatorDescriptor?.requiresValue !== false;

  const emit = useCallback((operator: FilterOperator, start: string, end?: string) => {
    const descriptor = FILTER_OPERATORS.find(candidate => candidate.id === operator);
    if (descriptor?.requiresValue === false) {
      onChange({ operator, value: null });
      return;
    }
    if (!start) {
      onChange(null);
      return;
    }
    if (operator === 'between') {
      if (!end) return;
      onChange({
        operator,
        value: [serializeDateFilterValue(start, fieldType), serializeDateFilterValue(end, fieldType)],
      });
      return;
    }
    onChange({ operator, value: serializeDateFilterValue(start, fieldType) });
  }, [fieldType, onChange]);

  const handleOperatorChange = (operator: string) => {
    const nextOperator = operator as FilterOperator;
    setLocalOperator(nextOperator);
    if (nextOperator !== 'between') setLocalEndValue('');
    emit(nextOperator, localValue, localEndValue);
  };

  const handleClear = useCallback(() => {
    setLocalValue('');
    setLocalEndValue('');
    onChange(null);
  }, [onChange]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {allowedOperators.length > 1 && (
        <FilterOperatorMenu
          column={column}
          currentOperator={localOperator}
          onChange={handleOperatorChange}
          allowedOperators={allowedOperators}
          disabled={disabled}
        />
      )}
      {requiresValue && (
        <Box sx={{ display: 'flex', gap: 0.5, flex: 1, minWidth: 0 }}>
          <TextField
            type={inputType}
            size="small"
            fullWidth
            value={localValue}
            onChange={event => {
              const nextValue = event.target.value;
              setLocalValue(nextValue);
              emit(localOperator, nextValue, localEndValue);
            }}
            disabled={disabled}
            sx={{ minWidth: 0 }}
            slotProps={{ htmlInput: { 'aria-label': accessibleLabel } }}
          />
          {isRange && (
            <TextField
              type={inputType}
              size="small"
              fullWidth
              value={localEndValue}
              onChange={event => {
                const nextValue = event.target.value;
                setLocalEndValue(nextValue);
                emit(localOperator, localValue, nextValue);
              }}
              disabled={disabled}
              sx={{ minWidth: 0 }}
              slotProps={{ htmlInput: { 'aria-label': rangeEndLabel } }}
            />
          )}
        </Box>
      )}
      {(localValue || localEndValue || !requiresValue) && (
        <IconButton
          size="small"
          onClick={handleClear}
          disabled={disabled}
          aria-label={t('common:clear')}
          sx={{ flexShrink: 0 }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function createDateFilter() {
  return React.memo(DateColumnFilter);
}

registerFilterControl('date', () => createDateFilter());
registerFilterControl('datetime', () => createDateFilter());
